/**
 * Chat SMS dispatcher.
 *
 * The actual SMS is sent server-side via the `sendChatSms` Cloud Function
 * (which talks to SMS4Free with credentials in Firebase Secrets). The old
 * client-side path — POST to a Google Apps Script proxy with no-cors fetch
 * — was unable to read the gateway response and silently treated every
 * rejection as success. Routing through the Cloud Function gives us:
 *
 *   - Real status: SMS4Free's actual response (status, error message).
 *   - No CORS hacks: server-to-server fetch.
 *   - Consistent template: SMS body is built server-side too.
 *
 * The legacy `buildSmsText` / `toE164` helpers and the SmsGateway constant
 * are no longer used by this module — kept exported only because some
 * legacy callers still import them.
 */

import { getFirebase } from '../../core/firebase.js';
import { Firebase } from '../../app/config.js';
import { normalize as normalizePhone, toE164 } from '../../shared/phone.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('sms');

/**
 * @typedef {Object} SendSmsInput
 * @property {string} to               Recipient phone, any format — will be normalized.
 * @property {string} body             Plain text. Will be wrapped in the AutoMe template.
 * @property {string} [senderPhone]    Caller's phone, used to build the reply link when sharePhone is true.
 * @property {boolean} [sharePhone=true]  Include `השב ב- sms:+972...` line in the SMS.
 */

/**
 * @typedef {Object} SendSmsResult
 * @property {boolean} ok
 * @property {string} [error]
 */

/**
 * Send a chat SMS via the `sendChatSms` Cloud Function. The Cloud Function
 * builds the SMS template server-side, calls SMS4Free, and returns the real
 * SMS4Free status. Errors here reflect actual delivery failures, not opaque
 * no-cors responses.
 *
 * @param {SendSmsInput} input
 * @returns {Promise<SendSmsResult>}
 */
export async function send({ to, body, senderPhone = '', sharePhone = true }) {
  const phone = normalizePhone(to);
  if (!/^05\d{8}$/.test(phone)) return { ok: false, error: 'invalid-phone' };
  if (!body || !body.trim()) return { ok: false, error: 'empty-body' };

  log.info('dispatching SMS via Cloud Function to', phone, 'sharePhone=', sharePhone);

  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    const result = await functions.httpsCallable('sendChatSms')({
      to: phone,
      body: body.trim(),
      senderPhone: normalizePhone(senderPhone),
      sharePhone,
    });
    if (result && result.data && result.data.ok) {
      log.info('SMS delivered (status=', result.data.status, ')');
      return { ok: true };
    }
    return { ok: false, error: 'gateway_unknown' };
  } catch (err) {
    const e = /** @type {{ code?: string; message?: string }} */ (err);
    log.warn('sendChatSms failed:', e && (e.code || e.message));
    return { ok: false, error: e && (e.message || e.code) ? `${e.code || ''}: ${e.message || ''}` : 'send_failed' };
  }
}

// Re-export `toE164` for callers that still want it (history.view, etc).
export { toE164 };

/**
 * Human "time ago" string used by the legacy SMS confirmation popups.
 * Kept here so we can drop sms-chat.js entirely.
 *
 * @param {number | string | Date} when
 * @returns {string}
 */
export function timeAgo(when) {
  const t = typeof when === 'number' ? when : new Date(when).getTime();
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'עכשיו';
  const min = Math.floor(sec / 60);
  if (min < 60) return `לפני ${min} דק׳`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `לפני ${hr} שעות`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'אתמול';
  if (days < 7) return `לפני ${days} ימים`;
  const d = new Date(t);
  return d.toLocaleDateString('he-IL');
}
